import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import useFieldDashboard from "../../lib/fieldDashboard/useFieldDashboard";
import dayjs, { Dayjs } from "dayjs";
import {
    getClassificationLabel,
    Imagery,
    ModelClassification,
    ImageryType,
} from "@/types/imagery";
import classNames from "classnames";
import {
    Button,
    DatePicker,
    DateValue,
    getKeyValue,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Spinner,
    TableCell,
    Table,
    TableBody,
    TableColumn,
    TableHeader,
    TableRow,
    ModalContent,
    ModalBody,
    Modal,
    ModalHeader,
    Select,
    SelectItem,
    Chip,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft,
    faArrowRight,
    faCheck,
    faClose,
    faMagnifyingGlass,
    faStar,
    faThumbsDown,
    faXmark,
    faEye,
} from "@fortawesome/free-solid-svg-icons";
import { parseDate, getLocalTimeZone } from "@internationalized/date";
import useIsMobile from "@/lib/utils/useIsMobile";
import utc from "dayjs/plugin/utc";
import NStatusChip from "../common/NStatusChip";
import { roundTo } from "@/lib/utils/numbers";
import useRequest from "@/lib/request/useRequest";
import useAuth from "@/lib/auth/useAuth";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import { useTranslation } from "react-i18next";
import { Resource } from "@/types/user";

dayjs.extend(utc);

const COLUMNS = [
    {
        key: "source",
        label: "Source",
    },
    {
        key: "time",
        label: "Time",
    },
    {
        key: "usable",
        label: "Usable",
    },
    {
        key: "view",
        label: "View",
    },
    {
        key: "preferred",
        label: "Preferred",
    },
];

const SENTINEL_COLUMNS = [
    {
        key: "source",
        label: "Source",
    },
    {
        key: "time",
        label: "Time",
    },
    {
        key: "n_status",
        label: "N Status",
    },
    {
        key: "quality",
        label: "Quality",
    },
    {
        key: "si",
        label: "SI",
    },
    {
        key: "view",
        label: "View",
    },
    {
        key: "actions",
        label: "Actions",
    },
];
function ImageTable({
    images,
    selectedImage,
    setSelectedImage,
    imageType,
    setImageType,
    onScore,
    onPrefer,
}: {
    images: Imagery[];
    selectedImage?: Imagery;
    setSelectedImage?: (image?: Imagery) => void;
    imageType?: ImageryType;
    setImageType?: (imageType: ImageryType) => void;
    onScore?: (imagery: Imagery, classification: ModelClassification) => void;
    onPrefer?: (imagery: Imagery, manually_preferred: boolean) => void;
}) {
    const { isAtLeastDetail, isAtLeastSentinel } = useAuth();

    const columns = useMemo(() => {
        if (isAtLeastSentinel(Resource.IMAGERY_TIMELINE)) {
            return SENTINEL_COLUMNS;
        } else if (isAtLeastDetail(Resource.IMAGERY_TIMELINE)) {
            return COLUMNS;
        }
        return [];
    }, [isAtLeastDetail, isAtLeastSentinel]);

    const sortedImages = useMemo(() => {
        return images?.sort((a, b) => {
            return dayjs(b.capture_date).diff(dayjs(a.capture_date));
        });
    }, [images]);
    const { imageryOptions } = useFieldDashboard() ?? {};

    return (
        <Table
            isStriped
            aria-label="image-table"
            classNames={{
                th: "px-[0.25rem]",
                td: "px-[0.25rem]",
                wrapper: "p-0 shadow-none rounded-none",
            }}
        >
            <TableHeader columns={columns}>
                {(column) => (
                    <TableColumn key={column.key}>{column.label}</TableColumn>
                )}
            </TableHeader>
            <TableBody
                items={sortedImages}
                emptyContent={"No images found"}
                loadingContent={<Spinner label="Loading..." />}
            >
                {images?.map((item) => (
                    <TableRow key={item.id}>
                        {(columnKey) => {
                            if (columnKey === "source") {
                                return (
                                    <TableCell className="text-xs">
                                        {
                                            item.imagery_subscription
                                                .provider_name
                                        }
                                    </TableCell>
                                );
                            }
                            if (columnKey === "time") {
                                return (
                                    <TableCell className="text-xs">
                                        {dayjs
                                            .utc(item.capture_date)
                                            .local()
                                            .format("hh:mm.ss a")}
                                    </TableCell>
                                );
                            }
                            if (columnKey === "n_status") {
                                return (
                                    <TableCell className="text-xs max-w-[5rem] overflow-hidden text-ellipsis whitespace-nowrap">
                                        {item.n_status ? (
                                            <NStatusChip
                                                nStatus={item.n_status}
                                            />
                                        ) : (
                                            "--"
                                        )}
                                    </TableCell>
                                );
                            }
                            if (columnKey === "quality") {
                                return (
                                    <TableCell className="flex flex-col text-xs">
                                        {item.usable ? (
                                            <span className="flex flex-row w-full justify-between">
                                                Usable:
                                                <FontAwesomeIcon
                                                    icon={faCheck}
                                                    className="text-green-500"
                                                />
                                            </span>
                                        ) : (
                                            <span className="flex flex-row w-full justify-between">
                                                Usable:
                                                <FontAwesomeIcon
                                                    icon={faXmark}
                                                    className="text-red-500"
                                                />
                                            </span>
                                        )}
                                        {"Model: " +
                                            getClassificationLabel(
                                                item.model_classification,
                                            )}
                                    </TableCell>
                                );
                            }
                            if (columnKey === "view") {
                                return (
                                    <TableCell className="text-xs">
                                        {item.id === selectedImage?.id ? (
                                            <Select
                                                variant="bordered"
                                                size="sm"
                                                className="min-w-[6rem]"
                                                selectedKeys={
                                                    imageType ? [imageType] : []
                                                }
                                                onChange={(key) => {
                                                    setSelectedImage?.(item);
                                                    setImageType?.(
                                                        key.target
                                                            .value as ImageryType,
                                                    );
                                                }}
                                            >
                                                {(imageryOptions ?? []).map(
                                                    (option) => (
                                                        <SelectItem
                                                            className="min-w-[6rem]"
                                                            key={option.key}
                                                        >
                                                            {option.label}
                                                        </SelectItem>
                                                    ),
                                                )}
                                            </Select>
                                        ) : (
                                            <Button
                                                variant="flat"
                                                className="w-full"
                                                size="sm"
                                                onPress={() => {
                                                    setSelectedImage?.(item);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faEye} />
                                                View
                                            </Button>
                                        )}
                                    </TableCell>
                                );
                            }
                            if (columnKey === "actions") {
                                return (
                                    <TableCell className="text-xs">
                                        <div className="flex flex-row gap-2">
                                            <Button
                                                size="sm"
                                                isIconOnly
                                                color="success"
                                                className="tiny-icon-button"
                                                onPress={() => {
                                                    onScore?.(
                                                        item,
                                                        ModelClassification.good,
                                                    );
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faCheck}
                                                />
                                            </Button>
                                            <Button
                                                size="sm"
                                                isIconOnly
                                                color="danger"
                                                className="tiny-icon-button"
                                                onPress={() => {
                                                    onScore?.(
                                                        item,
                                                        ModelClassification.bad,
                                                    );
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faThumbsDown}
                                                />
                                            </Button>
                                            <Button
                                                size="sm"
                                                isIconOnly
                                                isDisabled={!item.usable}
                                                variant="light"
                                                className="tiny-icon-button"
                                                onPress={() => {
                                                    onPrefer?.(
                                                        item,
                                                        !item.active_for_day,
                                                    );
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faStar}
                                                    color={
                                                        item.active_for_day
                                                            ? "#FFD700"
                                                            : "black"
                                                    }
                                                />
                                            </Button>
                                        </div>
                                    </TableCell>
                                );
                            }
                            if (columnKey === "si") {
                                return (
                                    <TableCell className="text-xs">
                                        <div className="flex flex-col">
                                            <div className="flex flex-row justify-between">
                                                Min:{" "}
                                                {item.si_min
                                                    ? roundTo(item.si_min, 3)
                                                    : "--"}{" "}
                                            </div>
                                            <div className="flex flex-row justify-between">
                                                Mean:{" "}
                                                {item.si_mean
                                                    ? roundTo(item.si_mean, 3)
                                                    : "--"}
                                            </div>
                                        </div>
                                    </TableCell>
                                );
                            }
                            if (columnKey === "preferred") {
                                return (
                                    <TableCell className="text-xs">
                                        <Button
                                            size="sm"
                                            isIconOnly
                                            isDisabled={!item.usable}
                                            variant="light"
                                            className="tiny-icon-button"
                                            onPress={() => {
                                                onPrefer?.(
                                                    item,
                                                    !item.active_for_day,
                                                );
                                            }}
                                        >
                                            <FontAwesomeIcon
                                                icon={faStar}
                                                color={
                                                    item.active_for_day
                                                        ? "#FFD700"
                                                        : "black"
                                                }
                                            />
                                        </Button>{" "}
                                    </TableCell>
                                );
                            }
                            if (columnKey == "usable") {
                                return (
                                    <TableCell className="text-xs">
                                        <Chip
                                            size="sm"
                                            color={
                                                item.usable
                                                    ? "secondary"
                                                    : "danger"
                                            }
                                        >
                                            {item.usable ? "Yes" : "No"}
                                        </Chip>
                                    </TableCell>
                                );
                            }
                            return (
                                <TableCell className="text-xs">
                                    {getKeyValue(item, columnKey) ?? "--"}
                                </TableCell>
                            );
                        }}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function ImageryTimeline() {
    const { t } = useTranslation("messages");
    const { isMobile } = useIsMobile();
    const NUMBER_OF_DAYS = useMemo(() => (isMobile ? 6 : 8), [isMobile]);
    const {
        filteredImagery,
        fieldImageryQuery,
        selectedCropCycle,
        setSelectedImage,
        selectedImage,
        imageType,
        setImageType,
    } = useFieldDashboard() ?? {};
    const { useMutateRequest } = useRequest();
    const { isAtLeastDetail } = useAuth();

    const [timelineStartdate, setTimelineStartDate] = useState<Dayjs>(
        selectedImage
            ? dayjs(selectedImage.capture_date).subtract(
                  NUMBER_OF_DAYS - 1,
                  "day",
              )
            : dayjs().subtract(NUMBER_OF_DAYS - 1, "day"),
    );

    const imagesInCycle = useMemo(
        () =>
            filteredImagery?.filter((image) => {
                const captureDate = dayjs(image.capture_date);
                if (!selectedCropCycle) return true;
                return (
                    captureDate.isAfter(
                        dayjs(selectedCropCycle.start_date).subtract(1, "days"),
                    ) &&
                    captureDate.isBefore(
                        (selectedCropCycle.end_date
                            ? dayjs(selectedCropCycle.end_date)
                            : getTodayWithTimeTravel()
                        ).add(1, "days"),
                    )
                );
            }) ?? [],
        [filteredImagery, selectedCropCycle],
    );

    useEffect(() => {
        if (!imagesInCycle.length) {
            setSelectedImage?.(undefined);
            return;
        }

        const mostRecentImage = imagesInCycle.reduce((prev, current) =>
            new Date(current.capture_date) > new Date(prev.capture_date)
                ? current
                : prev,
        );
        if (mostRecentImage) {
            setSelectedImage?.(mostRecentImage);
            setTimelineStartDate(
                dayjs(mostRecentImage.capture_date).subtract(5, "days"),
            );
        }
    }, [imagesInCycle, setSelectedImage]);

    const timelineDays = useMemo(() => {
        return Array(NUMBER_OF_DAYS)
            .fill(0)
            .map((_val, index) => {
                return timelineStartdate.add(index, "days");
            });
    }, [NUMBER_OF_DAYS, timelineStartdate]);

    const allImagesByDate = useMemo(() => {
        return (
            fieldImageryQuery?.data?.reduce<{ [x: string]: Imagery[] }>(
                (acc, im) => {
                    const dateStr = dayjs
                        .utc(im.capture_date)
                        .local()
                        .format("YYYY-MM-DD");
                    if (acc[dateStr]) {
                        acc[dateStr].push(im);
                    } else {
                        acc[dateStr] = [im];
                    }
                    return acc;
                },
                {},
            ) ?? {}
        );
    }, [fieldImageryQuery?.data]);

    const imagesByDate = useMemo(() => {
        return imagesInCycle.reduce<{ [x: string]: Imagery }>((acc, im) => {
            acc[dayjs.utc(im.capture_date).local().format("YYYY-MM-DD")] = im;
            return acc;
        }, {});
    }, [imagesInCycle]);

    const earliestCropCycleStart = selectedCropCycle?.start_date;

    const isDateUnavailable = useCallback(
        (date: DateValue) => {
            const d = dayjs(date.toDate(getLocalTimeZone())).format(
                "YYYY-MM-DD",
            );
            return !imagesByDate[d];
        },
        [imagesByDate],
    );

    const [showDetails, setShowDetails] = useState(false);

    const updateImagery = useMutateRequest({
        successMessage: t("success.imagery_updated"),
        errorMessage: t("error.failed_to_update_imagery"),
    });

    const onScore = useCallback(
        (imagery: Imagery, classification: ModelClassification) => {
            updateImagery.mutate(
                {
                    url: `imagery/${imagery.id}/score/`,
                    data: {
                        user_classification: classification,
                    },
                    method: "put",
                },
                {
                    onSuccess: () => {
                        fieldImageryQuery?.refetch();
                    },
                },
            );
        },
        [fieldImageryQuery, updateImagery],
    );

    const onPrefer = useCallback(
        (imagery: Imagery, manually_preferred: boolean) => {
            updateImagery.mutate(
                {
                    url: `imagery/${imagery.id}/manually_preferred/`,
                    data: {
                        manually_preferred,
                    },
                    method: "put",
                },
                {
                    onSuccess: () => {
                        fieldImageryQuery?.refetch();
                    },
                },
            );
        },
        [fieldImageryQuery, updateImagery],
    );

    return (
        <div className="flex flex-col  justify-center items-center scale-80 md:scale-100">
            <div className=" flex flex-row gap-2 p-3 rounded-lg items-center">
                <Button
                    isIconOnly
                    className="bg-white"
                    variant="solid"
                    onPress={() => {
                        setTimelineStartDate((d) =>
                            d.subtract(NUMBER_OF_DAYS, "days"),
                        );
                    }}
                    aria-label="Previous"
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                </Button>
                {timelineDays.map((day) => {
                    const imageOnDate = imagesByDate[day.format("YYYY-MM-DD")];
                    const imageUsable = imageOnDate?.usable;
                    const allImagesOnDay =
                        allImagesByDate[day.format("YYYY-MM-DD")] ?? [];
                    const selected =
                        dayjs(selectedImage?.capture_date).format(
                            "YYYY-MM-DD",
                        ) === day.format("YYYY-MM-DD");
                    return (
                        <Fragment key={day.format("YYYY-MM-DD")}>
                            {isMobile &&
                                showDetails &&
                                selected &&
                                isAtLeastDetail(Resource.IMAGERY_TIMELINE) && (
                                    <Modal
                                        isOpen={
                                            isMobile &&
                                            showDetails &&
                                            isAtLeastDetail(
                                                Resource.IMAGERY_TIMELINE,
                                            ) &&
                                            selected
                                        }
                                        onClose={() => setShowDetails(false)}
                                        placement="bottom-center"
                                        closeButton
                                        scrollBehavior="inside"
                                    >
                                        <ModalContent>
                                            <ModalHeader className="p-2 font-semibold">
                                                {day.format("ll")}
                                            </ModalHeader>
                                            <ModalBody className="p-2">
                                                <ImageTable
                                                    images={
                                                        allImagesByDate[
                                                            day.format(
                                                                "YYYY-MM-DD",
                                                            )
                                                        ]
                                                    }
                                                    selectedImage={
                                                        selectedImage
                                                    }
                                                    setSelectedImage={
                                                        setSelectedImage
                                                    }
                                                    imageType={imageType}
                                                    setImageType={setImageType}
                                                    onScore={onScore}
                                                    onPrefer={onPrefer}
                                                />
                                            </ModalBody>
                                        </ModalContent>
                                    </Modal>
                                )}
                            <Popover
                                isOpen={selected && showDetails && !isMobile}
                                placement="top"
                                radius="sm"
                            >
                                <PopoverTrigger>
                                    <div
                                        onClick={() => {
                                            if (imageOnDate) {
                                                setSelectedImage?.(imageOnDate);
                                            }
                                        }}
                                        className={classNames({
                                            "border-[3px] bg-opacity-50  font-extrabold  rounded-sm flex flex-col justify-center items-center aspect-square h-12 ":
                                                true,
                                            "scale-110 ": selected,
                                            "border-red-500 text-red-500 bg-black bg-opacity-40":
                                                imageUsable &&
                                                imageOnDate?.n_status === "red",
                                            "border-green-500 text-green-500 bg-black bg-opacity-40":
                                                imageUsable &&
                                                imageOnDate?.n_status ===
                                                    "green",
                                            "border-yellow-500 text-yellow-500 bg-black bg-opacity-40":
                                                imageUsable &&
                                                imageOnDate?.n_status ===
                                                    "yellow",
                                            "border-white text-white bg-black bg-opacity-40":
                                                imageUsable &&
                                                imageOnDate &&
                                                !imageOnDate.n_status,
                                            "border-blue-400 text-blue-400 bg-black bg-opacity-40":
                                                imageOnDate && !imageUsable,
                                            "hover:scale-110 cursor-pointer":
                                                imageOnDate,
                                            "border-gray-900 text-gray-400 hover:scale-100 bg-black bg-opacity-40":
                                                !imageOnDate,
                                        })}
                                    >
                                        <div>{day.format("MMM")}</div>
                                        <div>{day.format("DD")}</div>
                                        {selected &&
                                            isAtLeastDetail(
                                                Resource.IMAGERY_TIMELINE,
                                            ) &&
                                            imageOnDate && (
                                                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-[120%]">
                                                    <Button
                                                        size="sm"
                                                        isIconOnly
                                                        color="primary"
                                                        onPress={() => {
                                                            if (
                                                                isAtLeastDetail(
                                                                    Resource.IMAGERY_TIMELINE,
                                                                )
                                                            ) {
                                                                setShowDetails(
                                                                    !showDetails,
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={
                                                                faMagnifyingGlass
                                                            }
                                                        />
                                                    </Button>
                                                </div>
                                            )}
                                    </div>
                                </PopoverTrigger>
                                <PopoverContent>
                                    <div className="flex flex-col gap-1">
                                        <div className="flex flex-row justify-between items-center">
                                            <div className="font-semibold">
                                                {day.format("ll")}
                                            </div>
                                            <Button
                                                size="sm"
                                                isIconOnly
                                                variant="flat"
                                                onPress={() => {
                                                    setShowDetails(false);
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faClose}
                                                />
                                            </Button>
                                        </div>

                                        <ImageTable
                                            images={allImagesOnDay}
                                            selectedImage={selectedImage}
                                            setSelectedImage={setSelectedImage}
                                            imageType={imageType}
                                            setImageType={setImageType}
                                            onScore={onScore}
                                            onPrefer={onPrefer}
                                        />
                                    </div>
                                </PopoverContent>
                            </Popover>
                        </Fragment>
                    );
                })}

                <Button
                    isIconOnly
                    className="bg-white"
                    variant="solid"
                    onPress={() => {
                        setTimelineStartDate((d) =>
                            d.add(NUMBER_OF_DAYS, "days"),
                        );
                    }}
                    aria-label="Next"
                >
                    <FontAwesomeIcon icon={faArrowRight} />
                </Button>
            </div>
            <div className="flex flex-row justify-center items-center">
                <DatePicker
                    size="sm"
                    aria-label="imagery timeline date picker"
                    minValue={
                        earliestCropCycleStart
                            ? parseDate(earliestCropCycleStart)
                            : null
                    }
                    showMonthAndYearPickers={true}
                    isDateUnavailable={isDateUnavailable}
                    value={
                        selectedImage
                            ? parseDate(
                                  dayjs(selectedImage?.capture_date).format(
                                      "YYYY-MM-DD",
                                  ),
                              )
                            : null
                    }
                    onChange={(e) => {
                        const d = dayjs(e?.toDate(getLocalTimeZone()));
                        setTimelineStartDate(d);
                        const imOnDate = imagesByDate[d.format("YYYY-MM-DD")];
                        if (imOnDate) setSelectedImage?.(imOnDate);
                    }}
                />
            </div>
        </div>
    );
}
export default ImageryTimeline;
